import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AuthLayout from '../components/AuthLayout';
import SignInLeftVisual from '../components/auth/SignInLeftVisual';
import Card from '../ui/Card';
import Input from '../ui/Input';
import Button from '../ui/Button';
import AuthFormFooter from '../components/auth/AuthFormFooter';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fadeInUp, staggerContainer, staggerItem } from '../utils/animations';
import { MOBILE_FORMAT_HINT, validateMobile } from '../utils/authValidation';
import './AuthPages.css';

function SignIn() {
  const navigate = useNavigate();
  const { signin } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({ userName: '', mobileNumber: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(function (prev) {
      return { ...prev, [name]: value };
    });
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validateMobile(form.mobileNumber)) {
      setError('Please enter a valid mobile number (10–15 digits, with or without country code).');
      setLoading(false);
      return;
    }

    try {
      await signin(form.userName, form.password);
      toast('Signed in successfully.', 'success');
      navigate('/app');
    } catch (err) {
      let errorMessage = 'Sign in failed. Please check your credentials.';
      
      // Provide more specific error messages
      if (err.code === 'NotAuthorizedException') {
        errorMessage = 'Incorrect username or password.';
      } else if (err.code === 'UserNotConfirmedException') {
        errorMessage = 'Please verify your email address before signing in.';
      } else if (err.code === 'UserNotFoundException') {
        errorMessage = 'No account found with this username.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      toast(errorMessage, 'error');
      setLoading(false);
    }
  }

  return (
    <AuthLayout leftContent={<SignInLeftVisual />}>
      <motion.div {...fadeInUp}>
        <Card className="ui-card--glass">
          <form className="auth-form" onSubmit={handleSubmit}>
            <p className="auth-form-welcome">Welcome Back!</p>
            <h2 className="auth-form-title">Sign In</h2>

            <motion.div
              className="auth-form-fields"
              variants={staggerContainer}
              initial="initial"
              animate="animate"
            >
              <motion.div variants={staggerItem}>
                <Input
                  label="User Name"
                  name="userName"
                  type="text"
                  value={form.userName}
                  onChange={handleChange}
                  placeholder="User Name"
                  autoComplete="username"
                  required
                  glass
                />
              </motion.div>
              <motion.div variants={staggerItem}>
                <Input
                  label="Mobile Number"
                  name="mobileNumber"
                  type="tel"
                  value={form.mobileNumber}
                  onChange={handleChange}
                  placeholder="e.g. +1234567890"
                  autoComplete="tel"
                  required
                  glass
                />
                <p className="auth-field-hint">{MOBILE_FORMAT_HINT}</p>
              </motion.div>
              <motion.div className="auth-password-row" variants={staggerItem}>
                <Input
                  label="Password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Password"
                  autoComplete="current-password"
                  required
                  glass
                />
              </motion.div>
            </motion.div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="auth-form-error"
                style={{ color: 'var(--error-color, #ff4444)', marginBottom: '1rem', fontSize: '0.875rem' }}
              >
                {error}
              </motion.div>
            )}

            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                type="submit"
                variant="primary"
                fullWidth
                className="auth-form-submit"
                disabled={loading}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </motion.div>

            <AuthFormFooter
              text="Don't have an account?"
              linkTo="/signup"
              linkLabel="Sign Up"
            />
          </form>
        </Card>
      </motion.div>
    </AuthLayout>
  );
}

export default SignIn;
